type BrandDotsPreloaderProps = {
  /** Visible caption under the dots (also exposed to assistive tech when announce is true) */
  label?: string
  className?: string
  /** When false, dots are decorative; parent should expose loading (e.g. section aria-live). */
  announce?: boolean
  size?: "default" | "compact"
}

const ORBIT_ANGLES_DEG = [0, 120, 240] as const

/** Diameters px per vertex: white (outline) smallest → black → crimson largest (Figma-style hierarchy). */
const DOT_DIAMETERS_PX = {
  compact: [9, 11, 14] as const,
  default: [11, 14, 18] as const,
} as const

type OrbitDotStyle = {
  pulseClass: string
  dotClass: string
}

const ORBIT_DOT_STYLES: OrbitDotStyle[] = [
  {
    pulseClass: "motion-safe:animate-brand-pulse-1 motion-reduce:animate-none",
    dotClass: "rounded-full bg-white border-neutral-900 shadow-sm ring-1 ring-neutral-900/15",
  },
  {
    pulseClass: "motion-safe:animate-brand-pulse-2 motion-reduce:animate-none",
    dotClass: "rounded-full bg-neutral-900",
  },
  {
    pulseClass: "motion-safe:animate-brand-pulse-3 motion-reduce:animate-none",
    dotClass: "rounded-full bg-brand-500",
  },
]

/** Polar offset from hub: 0° = top, clockwise (screen coords, y down). */
const vertexOffsetPx = (angleDeg: number, orbitR: number) => {
  const rad = (angleDeg * Math.PI) / 180
  return {
    x: orbitR * Math.sin(rad),
    y: -orbitR * Math.cos(rad),
  }
}

/**
 * Three brand dots on a 120° orbit; the whole group rotates clockwise; each dot pulses.
 * Dots are positioned with left/top only so parent `transform` from spin is not fighting child transforms.
 */
export const BrandDotsPreloader = ({
  label,
  className = "",
  announce = true,
  size = "default",
}: BrandDotsPreloaderProps) => {
  const isCompact = size === "compact"
  const diameters = isCompact ? DOT_DIAMETERS_PX.compact : DOT_DIAMETERS_PX.default
  const maxDotPx = Math.max(...diameters)
  const orbitR = isCompact ? 11 : 14
  const orbitBox = isCompact ? "h-14 w-14" : "h-16 w-16"
  const spinSize = 2 * orbitR + maxDotPx

  const dots = (
    <div className={`relative flex shrink-0 items-center justify-center ${orbitBox}`} aria-hidden>
      <div
        className="relative animate-spin"
        style={{ width: spinSize, height: spinSize, animationDuration: "2.4s" }}
      >
        {ORBIT_ANGLES_DEG.map((angleDeg, i) => {
          const cfg = ORBIT_DOT_STYLES[i]!
          const dotPx = diameters[i]!
          const { x, y } = vertexOffsetPx(angleDeg, orbitR)
          return (
            <span
              key={angleDeg}
              className="absolute"
              style={{
                left: `calc(50% + ${x}px - ${dotPx / 2}px)`,
                top: `calc(50% + ${y}px - ${dotPx / 2}px)`,
                width: dotPx,
                height: dotPx,
              }}
            >
              <span
                className={`block h-full w-full ${cfg.pulseClass} ${cfg.dotClass} ${i === 0 ? (isCompact ? "border border-neutral-900" : "border-2 border-neutral-900") : ""}`}
              />
            </span>
          )
        })}
      </div>
    </div>
  )

  const stackGap = isCompact ? "gap-2" : "gap-4"
  const labelClass = isCompact ? "text-xs font-medium" : "text-sm font-medium"

  const body = (
    <>
      {dots}
      {label ? <span className={labelClass}>{label}</span> : null}
    </>
  )

  if (!announce) {
    return (
      <div
        className={`flex flex-col items-center justify-center ${stackGap} text-gray-600 ${className}`}
        aria-hidden
      >
        {body}
      </div>
    )
  }

  return (
    <div
      className={`flex flex-col items-center justify-center ${stackGap} text-gray-600 ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {body}
    </div>
  )
}
